import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth, API } from '../App';
import axios from 'axios';
import { getToken } from '../utils/auth';
import BottomNavigation from '../components/BottomNavigation';
import LanguageToggle from '../components/LanguageToggle';
import BusinessMap from '../components/BusinessMap';
import FilterPanel from '../components/FilterPanel';
import { Search, MapPin, SlidersHorizontal, Heart, Star, X, ShieldCheck, Bell, HeartHandshake, Navigation, Clock, Share2, Map as MapIcon } from 'lucide-react';
import { tf } from '../utils/translateFeature';
import { useNotifications } from '../contexts/NotificationContext';
import NearbyDealsToggle from '../components/NearbyDealsToggle';
import CommunityPartnerCard from '../components/CommunityPartnerCard';
import { useFlags } from '../hooks/useFlags';
import FoundingBusinessBadge from '../components/FoundingBusinessBadge';

import FollowPromptBanner from '../components/FollowPromptBanner';
import SeededListingsBatchNotice from '../components/SeededListingsBatchNotice';
import TownPill from '../components/TownPill';
import NotificationBell from '../components/NotificationBell';
import { formatPlaceName } from '../utils/placeName';
import { shareFind2Day } from '../utils/shareApp';

const Discover = () => {
  const navigate = useNavigate();
  const flags = useFlags();
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const [businesses, setBusinesses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [activeFilters, setActiveFilters] = useState({});
  const [activeFilterCount, setActiveFilterCount] = useState(0);
  // showNotifSettings kept for potential re-use; the old bell now navigates to /notifications inbox
  const [showNotifSettings, setShowNotifSettings] = useState(false); // eslint-disable-line no-unused-vars
  const [listingTypeFilter, setListingTypeFilter] = useState('');
  const notifications = useNotifications();
  const [favouriteIds, setFavouriteIds] = useState(new Set());
  const [favToast, setFavToast] = useState('');

  // Nearest Me state
  const [nearestMe, setNearestMe] = useState(false);
  const [openNow, setOpenNow] = useState(false);
  const [userCoords, setUserCoords] = useState(null);
  const [geoError, setGeoError] = useState('');
  // Stage 2B — Discover map view + manual town fallback for Nearby (no GPS).
  const [mapView, setMapView] = useState(false);
  const [manualTown, setManualTown] = useState('');
  const [resolvingTown, setResolvingTown] = useState(false);
  // Stage 2B — worldwide cascading location filters (country/region/province/town).
  const [locationOptions, setLocationOptions] = useState([]);
  const [locFilter, setLocFilter] = useState({ country: '', region: '', province: '', town: '' });
  const [showLocation, setShowLocation] = useState(false);

  useEffect(() => {
    axios.get(`${API}/businesses/locations`)
      .then(r => setLocationOptions(r.data?.locations || []))
      .catch(() => setLocationOptions([]));
  }, []);

  const uniq = (arr) => Array.from(new Set(arr.filter(Boolean))).sort();
  const countryOpts = uniq(locationOptions.map(l => l.country));
  const regionOpts = uniq(locationOptions.filter(l => !locFilter.country || l.country === locFilter.country).map(l => l.region));
  const provinceOpts = uniq(locationOptions.filter(l => (!locFilter.country || l.country === locFilter.country) && (!locFilter.region || l.region === locFilter.region)).map(l => l.province));
  const townOpts = uniq(locationOptions.filter(l => (!locFilter.country || l.country === locFilter.country) && (!locFilter.region || l.region === locFilter.region) && (!locFilter.province || l.province === locFilter.province)).map(l => l.town));

  const applyLocation = (next) => {
    setLocFilter(next);
    fetchBusinesses(activeFilters, undefined, undefined, undefined, undefined, next);
  };

  const resolveTownOrigin = async () => {
    const q = manualTown.trim();
    if (!q) return;
    setResolvingTown(true);
    setGeoError('');
    try {
      const res = await axios.get(`${API}/geo/resolve`, { params: { q } });
      const coords = { lat: res.data.lat, lng: res.data.lng };
      setUserCoords(coords);
      setNearestMe(true);
      fetchBusinesses(activeFilters, undefined, undefined, coords, undefined, undefined, true);
    } catch {
      setGeoError(t('nearestMe.townNotFound', 'Could not find that town. Try a nearby larger town or add the country.'));
    } finally {
      setResolvingTown(false);
    }
  };

  useEffect(() => {
    fetchCategories();
    fetchBusinesses();
  }, []);

  // Iter 195 — Auto-search-on-type with a 300ms debounce. Matches modern
  // mobile search patterns (no separate "search" button). The first run is
  // skipped because fetchBusinesses() is already called on mount above.
  const skipFirstSearchRef = useRef(true);
  useEffect(() => {
    if (skipFirstSearchRef.current) {
      skipFirstSearchRef.current = false;
      return;
    }
    const handle = setTimeout(() => {
      fetchBusinesses(activeFilters);
    }, 300);
    return () => clearTimeout(handle);
  }, [searchTerm]);

  // Load user's favorites — logged-in users hit /user/favorites (account-bound,
  // cross-device); anonymous users mirror the device subscription's
  // favouriteBusinessIds (Phase 1 — no email needed to follow).
  useEffect(() => {
    if (user) {
      const token = getToken();
      if (!token) return;
      axios.get(`${API}/user/favorites`, { headers: { Authorization: `Bearer ${token}` } })
        .then(res => setFavouriteIds(new Set((res.data || []).map(b => b.id))))
        .catch(() => {});
    } else {
      setFavouriteIds(new Set(notifications?.preferences?.favouriteBusinessIds || []));
    }
  }, [user, notifications?.preferences?.favouriteBusinessIds]);

  const toggleFavourite = async (e, businessId) => {
    e.stopPropagation();
    // Logged-in users keep using the account-bound favourites endpoint.
    if (user) {
      const token = getToken();
      const isFav = favouriteIds.has(businessId);
      try {
        if (isFav) {
          await axios.delete(`${API}/user/favorites/${businessId}`, { headers: { Authorization: `Bearer ${token}` } });
          setFavouriteIds(prev => { const s = new Set(prev); s.delete(businessId); return s; });
          setFavToast(t('removedFromSaved', 'Removed'));
        } else {
          await axios.post(`${API}/user/favorites/${businessId}`, {}, { headers: { Authorization: `Bearer ${token}` } });
          setFavouriteIds(prev => new Set(prev).add(businessId));
          setFavToast(t('saved', 'Saved!'));
        }
        setTimeout(() => setFavToast(''), 1500);
      } catch (err) {
        console.error('Favourite toggle error:', err);
      }
      return;
    }
    // Anonymous flow — ensure a device subscription exists (this also
    // triggers the push-permission prompt on first tap), then toggle
    // favourite on the device record. No email or signup required.
    try {
      const subId = await notifications.ensureSubscription();
      if (!subId) {
        setFavToast(t('followNeedsNotifications', 'Allow notifications to follow businesses.'));
        setTimeout(() => setFavToast(''), 2500);
        return;
      }
      const isFav = favouriteIds.has(businessId);
      // Optimistic UI update for snappy feel
      setFavouriteIds(prev => {
        const s = new Set(prev);
        if (isFav) s.delete(businessId); else s.add(businessId);
        return s;
      });
      const data = await notifications.toggleFavourite(businessId);
      if (data === null) {
        // Roll back on failure
        setFavouriteIds(prev => {
          const s = new Set(prev);
          if (isFav) s.add(businessId); else s.delete(businessId);
          return s;
        });
        setFavToast(t('couldNotSave', 'Could not save — please try again.'));
      } else {
        setFavToast(data.favourited ? t('saved', 'Saved!') : t('removedFromSaved', 'Removed'));
      }
      setTimeout(() => setFavToast(''), 1500);
    } catch (err) {
      console.error('Anonymous favourite toggle error:', err);
    }
  };

  useEffect(() => {
    // Count active filters
    let count = 0;
    Object.entries(activeFilters).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        count += value.length;
      } else if (typeof value === 'boolean' && value) {
        count += 1;
      } else if (typeof value === 'string' && value && key === 'city') {
        count += 1;
      }
    });
    setActiveFilterCount(count);
  }, [activeFilters]);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/categories`);
      setCategories(response.data.categories);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchBusinesses = async (filters = activeFilters, categoryOverride, listingOverride, coords, openNowOverride, locOverride, nearestMeOverride) => {
    try {
      setLoading(true);
      const params = {};
      if (searchTerm) params.search = searchTerm;
      const cat = categoryOverride !== undefined ? categoryOverride : selectedCategory;
      if (cat) params.category = cat;

      // Stage 2B — worldwide location filters (never hide legacy no-location
      // businesses when no location filter is active).
      const lf = locOverride !== undefined ? locOverride : locFilter;
      if (lf.country) params.country = lf.country;
      if (lf.region) params.region = lf.region;
      if (lf.province) params.province = lf.province;
      if (lf.town) params.town = lf.town;
      
      // Listing type filter
      const lt = listingOverride !== undefined ? listingOverride : listingTypeFilter;
      if (lt) params.listingType = lt;
      
      // Nearest Me - pass user coordinates
      const loc = coords || userCoords;
      const nm = nearestMeOverride !== undefined ? nearestMeOverride : nearestMe;
      if (nm && loc) {
        params.lat = loc.lat;
        params.lng = loc.lng;
      }
      
      // Add filter params
      if (filters.groupSizes?.length) params.groupSizes = filters.groupSizes.join(',');
      if (filters.dietaryOptions?.length) params.dietaryOptions = filters.dietaryOptions.join(',');
      if (filters.eventTypes?.length) params.eventTypes = filters.eventTypes.join(',');
      if (filters.venueType?.length) params.venueType = filters.venueType.join(',');
      if (filters.additionalFeatures?.length) params.additionalFeatures = filters.additionalFeatures.join(',');
      if (filters.accessibilityFeatures?.length) params.accessibilityFeatures = filters.accessibilityFeatures.join(',');
      if (filters.city) params.city = filters.city;

      // Open now filter
      const on = openNowOverride !== undefined ? openNowOverride : openNow;
      if (on) params.openNow = true;

      const response = await axios.get(`${API}/businesses`, { params });
      setBusinesses(response.data);
    } catch (error) {
      console.error('Error fetching businesses:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    fetchBusinesses();
  };

  const handleApplyFilters = (filters) => {
    setActiveFilters(filters);
    fetchBusinesses(filters);
  };

  const toggleOpenNow = () => {
    const next = !openNow;
    setOpenNow(next);
    fetchBusinesses(activeFilters, undefined, undefined, undefined, next);
  };

  const toggleNearestMe = () => {
    if (nearestMe) {
      // Turn off
      setNearestMe(false);
      setUserCoords(null);
      setGeoError('');
      fetchBusinesses(activeFilters, undefined, undefined, null);
      return;
    }

    if (!navigator.geolocation) {
      setGeoError(t('nearestMe.notSupported', 'Geolocation is not supported by your browser'));
      return;
    }

    setGeoError('');
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const coords = { lat: position.coords.latitude, lng: position.coords.longitude };
        setUserCoords(coords);
        setNearestMe(true);
        fetchBusinesses(activeFilters, undefined, undefined, coords, undefined, undefined, true);
      },
      (err) => {
        if (err.code === 1) {
          setGeoError(t('nearestMe.denied', 'Location access denied. You can search by town instead.'));
        } else {
          setGeoError(t('nearestMe.error', 'Could not get your location. Try again.'));
        }
      },
      { enableHighAccuracy: false, timeout: 10000 }
    );
  };

  const clearAllFilters = () => {
    setActiveFilters({});
    setSelectedCategory('');
    setSearchTerm('');
    setListingTypeFilter('');
    setNearestMe(false);
    setUserCoords(null);
    setGeoError('');
    setLocFilter({ country: '', region: '', province: '', town: '' });
    fetchBusinesses({}, '', '', null, undefined, { country: '', region: '', province: '', town: '' });
  };

  const handleLogout = () => {
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-gray-50 pb-24">
      {/* Header */}
      <header className="bg-gradient-to-r from-purple-600 to-purple-700 text-white shadow-lg">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex justify-between items-center mb-4">
            <h1 className="text-2xl font-bold" data-testid="discover-title">{t('nav.discover', 'Discover')}</h1>
            <div className="flex items-center gap-3">
              <NotificationBell variant="on-purple" />
              <LanguageToggle />
              {user ? (
                <button
                  onClick={() => shareFind2Day(t)}
                  className="flex items-center gap-2 px-4 py-2 bg-white/20 hover:bg-white/30 rounded-lg transition-colors"
                  data-testid="share-app-button"
                  aria-label={t('shareApp', 'Share Find-2-Day')}
                  title={t('shareApp', 'Share Find-2-Day')}
                >
                  <Share2 className="h-4 w-4" />
                  <span className="hidden sm:inline">{t('shareApp', 'Share Find-2-Day')}</span>
                </button>
              ) : (
                <button
                  onClick={() => navigate('/signin')}
                  className="flex items-center gap-2 px-4 py-2 bg-white/20 hover:bg-white/30 rounded-lg transition-colors"
                  data-testid="signin-button"
                >
                  <span>{t('signIn', 'Sign In')}</span>
                </button>
              )}
            </div>
          </div>

          {/* Search bar + filter button — Iter 195 redesign:
              - Magnifying glass icon sits INSIDE the input (left side) so the
                affordance is immediately visible and tapping the icon area
                lands on the field.
              - Search runs automatically as you type (300ms debounce) AND
                still fires immediately on Enter.
              - Inline "×" clears the field and re-runs the search.
              - The separate magnifying-glass button has been removed —
                reduces thumb travel and matches modern mobile patterns.
              - Filters + Nearest Me remain as separate adjacent actions. */}
          <div className="flex gap-2 items-center flex-wrap sm:flex-nowrap">
            <TownPill variant="inline" />
            <div className="relative flex-1 min-w-[200px]">
              <Search
                className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-purple-400"
                aria-hidden="true"
              />
              <input
                type="search"
                inputMode="search"
                enterKeyHint="search"
                placeholder={t('searchPlaceholder', 'Search businesses...')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleSearch(); } }}
                className="w-full pl-10 pr-10 py-2 rounded-lg text-gray-900 focus:ring-2 focus:ring-white outline-none"
                data-testid="search-input"
                aria-label={t('searchPlaceholder', 'Search businesses...')}
              />
              {searchTerm && (
                <button
                  type="button"
                  onClick={() => setSearchTerm('')}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded-full text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors"
                  data-testid="search-clear-btn"
                  aria-label={t('clearSearch', 'Clear search')}
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
            <button
              onClick={() => setShowFilters(true)}
              className="relative px-4 py-2 bg-white/20 hover:bg-white/30 rounded-lg transition-colors"
              data-testid="filter-button"
              aria-label={t('filters', 'Filters')}
              title={t('filters', 'Filters')}
            >
              <SlidersHorizontal className="h-5 w-5" />
              {activeFilterCount > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                  {activeFilterCount}
                </span>
              )}
            </button>
            <button
              onClick={toggleNearestMe}
              className={`px-4 py-2 rounded-lg transition-colors flex items-center gap-1.5 ${
                nearestMe
                  ? 'bg-white text-purple-600 font-semibold'
                  : 'bg-white/20 hover:bg-white/30'
              }`}
              data-testid="nearest-me-btn"
              title={t('nearestMe.button', 'Nearest Me')}
              aria-label={t('nearestMe.button', 'Nearest Me')}
            >
              <Navigation className={`h-4 w-4 ${nearestMe ? 'fill-purple-600' : ''}`} />
              <span className="text-xs hidden sm:inline">{t('nearestMe.button', 'Nearest Me')}</span>
            </button>
            <button
              onClick={toggleOpenNow}
              className={`px-4 py-2 rounded-lg transition-colors flex items-center gap-1.5 ${
                openNow
                  ? 'bg-white text-emerald-600 font-semibold'
                  : 'bg-white/20 hover:bg-white/30'
              }`}
              data-testid="open-now-btn"
              title="Open now"
              aria-label="Open now"
              aria-pressed={openNow}
            >
              <Clock className={`h-4 w-4 ${openNow ? 'text-emerald-600' : ''}`} />
              <span className="text-xs hidden sm:inline">Open now</span>
            </button>
            <button
              onClick={() => setShowLocation(v => !v)}
              className={`px-4 py-2 rounded-lg transition-colors flex items-center gap-1.5 ${
                (showLocation || locFilter.country)
                  ? 'bg-white text-purple-600 font-semibold'
                  : 'bg-white/20 hover:bg-white/30'
              }`}
              data-testid="location-filter-toggle"
              title={t('locationFilterLabel', 'Location')}
              aria-label={t('locationFilterLabel', 'Location')}
              aria-pressed={showLocation}
            >
              <MapPin className="h-4 w-4" />
              <span className="text-xs hidden sm:inline">{t('locationFilterLabel', 'Location')}</span>
            </button>
            <button
              onClick={() => setMapView(v => !v)}
              className={`px-4 py-2 rounded-lg transition-colors flex items-center gap-1.5 ${
                mapView
                  ? 'bg-white text-purple-600 font-semibold'
                  : 'bg-white/20 hover:bg-white/30'
              }`}
              data-testid="map-view-toggle"
              title={t('mapView', 'Map view')}
              aria-label={t('mapView', 'Map view')}
              aria-pressed={mapView}
            >
              <MapIcon className="h-4 w-4" />
              <span className="text-xs hidden sm:inline">{t('mapView', 'Map')}</span>
            </button>
          </div>
          {geoError && (
            <div className="mt-2" data-testid="geo-fallback">
              <p className="text-xs text-amber-200">{geoError}</p>
              <div className="flex gap-2 mt-1.5">
                <input
                  type="text"
                  value={manualTown}
                  onChange={(e) => setManualTown(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); resolveTownOrigin(); } }}
                  placeholder={t('nearestMe.townPlaceholder', 'Enter a town, e.g. Lyon, France')}
                  className="flex-1 min-w-[160px] px-3 py-1.5 rounded-lg text-gray-900 text-sm outline-none"
                  data-testid="manual-town-input"
                />
                <button
                  onClick={resolveTownOrigin}
                  disabled={resolvingTown || !manualTown.trim()}
                  className="px-3 py-1.5 bg-white text-purple-600 text-sm font-semibold rounded-lg disabled:opacity-50"
                  data-testid="manual-town-submit"
                >
                  {resolvingTown ? t('loading', 'Loading…') : t('nearestMe.useTown', 'Use town')}
                </button>
              </div>
            </div>
          )}
        </div>
      </header>

      {/* Stage 2B — worldwide cascading location filter (collapsible, mobile-friendly) */}
      {showLocation && (
        <div className="bg-white border-b border-gray-200" data-testid="location-filter-panel">
          <div className="max-w-7xl mx-auto px-4 py-3 grid grid-cols-2 sm:grid-cols-4 gap-2">
            <select
              value={locFilter.country}
              onChange={(e) => applyLocation({ country: e.target.value, region: '', province: '', town: '' })}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm outline-none focus:ring-2 focus:ring-purple-500"
              data-testid="loc-country-select"
            >
              <option value="">{t('allCountries', 'All countries')}</option>
              {countryOpts.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
            <select
              value={locFilter.region}
              onChange={(e) => applyLocation({ ...locFilter, region: e.target.value, province: '', town: '' })}
              disabled={regionOpts.length === 0}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm outline-none focus:ring-2 focus:ring-purple-500 disabled:bg-gray-100"
              data-testid="loc-region-select"
            >
              <option value="">{t('allRegions', 'All regions')}</option>
              {regionOpts.map(r => <option key={r} value={r}>{r}</option>)}
            </select>
            <select
              value={locFilter.province}
              onChange={(e) => applyLocation({ ...locFilter, province: e.target.value, town: '' })}
              disabled={provinceOpts.length === 0}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm outline-none focus:ring-2 focus:ring-purple-500 disabled:bg-gray-100"
              data-testid="loc-province-select"
            >
              <option value="">{t('allProvinces', 'All provinces')}</option>
              {provinceOpts.map(p => <option key={p} value={p}>{p}</option>)}
            </select>
            <select
              value={locFilter.town}
              onChange={(e) => applyLocation({ ...locFilter, town: e.target.value })}
              disabled={townOpts.length === 0}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm outline-none focus:ring-2 focus:ring-purple-500 disabled:bg-gray-100"
              data-testid="loc-town-select"
            >
              <option value="">{t('allTowns', 'All towns')}</option>
              {townOpts.map(tn => <option key={tn} value={tn}>{tn}</option>)}
            </select>
          </div>
        </div>
      )}

      {/* Iter 172 — Removed the Iter 157 premium feature-cards row.
          Local Businesses is now businesses-only — no Your Alerts /
          Community Partner / Sponsored Deals widgets on this page.
          Those destinations remain available from the home grid and
          the Partners panel. */}

      {/* Active filters / Reset bar */}
      {(activeFilterCount > 0 || selectedCategory || searchTerm || nearestMe || locFilter.country) && (
        <div className="bg-purple-50 border-b border-purple-100">
          <div className="max-w-7xl mx-auto px-4 py-2">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm text-purple-700 font-medium">{t('activeFilters', 'Active Filters')}:</span>
              {selectedCategory && (
                <span className="inline-flex items-center gap-1 px-2 py-1 bg-purple-100 text-purple-700 text-xs rounded-full">
                  {selectedCategory}
                  <button onClick={() => { setSelectedCategory(''); fetchBusinesses(activeFilters, ''); }} data-testid="remove-category-filter">
                    <X className="h-3 w-3" />
                  </button>
                </span>
              )}
              {searchTerm && (
                <span className="inline-flex items-center gap-1 px-2 py-1 bg-purple-100 text-purple-700 text-xs rounded-full">
                  "{searchTerm}"
                  <button onClick={() => { setSearchTerm(''); setTimeout(() => fetchBusinesses(activeFilters), 0); }} data-testid="remove-search-filter">
                    <X className="h-3 w-3" />
                  </button>
                </span>
              )}
              {nearestMe && (
                <span className="inline-flex items-center gap-1 px-2 py-1 bg-purple-100 text-purple-700 text-xs rounded-full">
                  <Navigation className="h-3 w-3" />
                  {t('nearestMe.button', 'Nearest Me')}
                  <button onClick={() => { setNearestMe(false); setUserCoords(null); fetchBusinesses(activeFilters, undefined, undefined, null); }} data-testid="remove-nearest-filter">
                    <X className="h-3 w-3" />
                  </button>
                </span>
              )}
              {Object.entries(activeFilters).map(([key, values]) => {
                if (Array.isArray(values) && values.length > 0) {
                  return values.map(v => (
                    <span key={`${key}-${v}`} className="inline-flex items-center gap-1 px-2 py-1 bg-purple-100 text-purple-700 text-xs rounded-full">
                      {tf(t, v)}
                      <button onClick={() => {
                        const newFilters = { ...activeFilters, [key]: values.filter(x => x !== v) };
                        setActiveFilters(newFilters);
                        fetchBusinesses(newFilters);
                      }}>
                        <X className="h-3 w-3" />
                      </button>
                    </span>
                  ));
                }
                return null;
              })}
              <button
                onClick={clearAllFilters}
                className="ml-auto px-3 py-1.5 bg-purple-600 text-white text-xs font-semibold rounded-full hover:bg-purple-700 transition-colors"
                data-testid="reset-all-filters-btn"
              >
                {t('resetFilters', 'Reset Filters')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        {flags.affiliatesEnabled && (
          <div className="mb-6" data-testid="discover-community-partner">
            <CommunityPartnerCard />
          </div>
        )}
        {/* Iter 172 — Removed `<CommunityToolsCard />` (Safety / Emergency /
            Bank Holidays). Local Businesses is businesses-only. Those
            destinations remain reachable from the home grid and Profile. */}

        {loading ? (
          <div className="text-center py-12">
            <div className="animate-pulse">
              <div className="h-8 bg-gray-200 rounded w-48 mx-auto mb-4"></div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {[1,2,3].map(i => (
                  <div key={i} className="bg-gray-200 h-64 rounded-lg"></div>
                ))}
              </div>
            </div>
          </div>
        ) : businesses.length === 0 ? (
          <div className="text-center py-12">
            <div className="bg-white rounded-lg shadow p-8">
              <p className="text-gray-500 text-lg mb-4">
                {t('noBusinessesFound', 'No businesses found.')}
              </p>
              <p className="text-gray-400 text-sm mb-6">
                {activeFilterCount > 0 
                  ? t('tryDifferentFilters', 'Try adjusting your filters or search terms.')
                  : t('businessesWillAppear', 'Businesses will appear here once approved by admin.')}
              </p>
              {activeFilterCount > 0 ? (
                <button
                  onClick={clearAllFilters}
                  className="px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                >
                  {t('clearFilters', 'Clear Filters')}
                </button>
              ) : (
                <button
                  onClick={() => navigate('/')}
                  className="px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                >
                  {t('backToHome', 'Back to Home')}
                </button>
              )}
            </div>
          </div>
        ) : (
          <>
            <h2 className="text-xl font-semibold text-gray-900 mb-4">
              {businesses.length} {businesses.length === 1 ? t('businessFound', 'Business Found') : t('businessesFound', 'Businesses Found')}
            </h2>
            
            <FollowPromptBanner />

            <SeededListingsBatchNotice businesses={businesses} />

            {mapView && (
              <div className="mb-6" data-testid="discover-map-view">
                {(() => {
                  const markers = businesses
                    .filter(b => typeof b.latitude === 'number' && typeof b.longitude === 'number')
                    .map(b => ({ id: b.id, lat: b.latitude, lng: b.longitude, title: b.businessName }));
                  return markers.length > 0 ? (
                    <BusinessMap markers={markers} height={480} onMarkerClick={(id) => navigate(`/business/${id}`)} />
                  ) : (
                    <p className="text-gray-500 text-sm bg-white rounded-lg p-6 text-center" data-testid="map-no-coords">
                      {t('mapNoCoords', 'None of these businesses have map locations yet. Switch to list view to see all results.')}
                    </p>
                  );
                })()}
              </div>
            )}

            <div className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 ${mapView ? 'hidden' : ''}`}>
              {businesses.map((business) => (
                <div
                  key={business.id}
                  onClick={() => navigate(`/business/${business.id}`)}
                  className="bg-white rounded-xl shadow hover:shadow-lg transition-all cursor-pointer overflow-hidden group"
                  data-testid={`business-card-${business.id}`}
                >
                  <div className="relative h-48 sm:h-56 overflow-hidden bg-gradient-to-br from-purple-100 to-purple-200">
                    {business.photos && business.photos.length > 0 ? (
                      <img
                        src={business.photos[0]}
                        alt={business.businessName}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        style={{ objectPosition: 'center 30%' }}
                        loading="lazy"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <span className="text-purple-300 text-6xl font-bold">
                          {business.businessName.charAt(0)}
                        </span>
                      </div>
                    )}
                    <div className="absolute top-3 right-3">
                      <button 
                        onClick={(e) => toggleFavourite(e, business.id)}
                        className={`p-2 rounded-full transition-all ${
                          favouriteIds.has(business.id)
                            ? 'bg-red-50 scale-110'
                            : 'bg-white/90 hover:bg-white'
                        }`}
                        data-testid={`favorite-btn-${business.id}`}
                      >
                        <Heart className={`h-4 w-4 transition-colors ${
                          favouriteIds.has(business.id)
                            ? 'fill-red-500 text-red-500'
                            : 'text-gray-600'
                        }`} />
                      </button>
                    </div>
                  </div>
                  
                  <div className="p-4">
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center gap-2 min-w-0">
                        <h3 className="text-lg font-semibold text-gray-900 line-clamp-1" data-testid={`business-name-${business.id}`}>
                          {business.businessName}
                        </h3>
                        <FoundingBusinessBadge plan={business.subscriptionPlan} variant="card" />
                        {business.closureStatus ? (
                          <span
                            className={`shrink-0 max-w-[180px] truncate text-[10px] font-semibold px-2 py-0.5 rounded-full ${
                              business.closureStatus.state === 'closed'
                                ? 'bg-red-100 text-red-700'
                                : 'bg-amber-100 text-amber-800'
                            }`}
                            title={business.closureStatus.message}
                            data-testid={`business-closure-badge-${business.id}`}
                          >
                            {business.closureStatus.message}
                          </span>
                        ) : business.upcomingClosure ? (
                          <span
                            className="shrink-0 max-w-[200px] truncate text-[10px] font-semibold px-2 py-0.5 rounded-full bg-blue-100 text-blue-700"
                            title={`Upcoming: ${business.upcomingClosure.message}`}
                            data-testid={`business-upcoming-closure-badge-${business.id}`}
                          >
                            Upcoming: {business.upcomingClosure.message}
                          </span>
                        ) : null}
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        {business.rating && (
                          <div className="flex items-center gap-1 text-sm">
                            <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                            <span className="font-medium">{business.rating}</span>
                          </div>
                        )}
                      </div>
                    </div>
                    
                    {/* Charity badge */}
                    {business.listingType === 'charity' && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-teal-100 text-teal-700 text-xs font-medium rounded-full mb-2" data-testid={`charity-badge-${business.id}`}>
                        <HeartHandshake className="h-3 w-3" />
                        {t('charity.charityBadge')}
                      </span>
                    )}
                    
                    {/* Unclaimed / Verified badge */}
                    {business.listingType !== 'charity' && business.source === 'seed' && !business.isClaimed ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-100 text-amber-700 text-xs font-medium rounded-full mb-2" data-testid={`unclaimed-badge-${business.id}`}>
                        <ShieldCheck className="h-3 w-3" />
                        {t('unclaimedListing')}
                      </span>
                    ) : business.listingType !== 'charity' && business.isClaimed ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-100 text-green-700 text-xs font-medium rounded-full mb-2" data-testid={`verified-badge-${business.id}`}>
                        <ShieldCheck className="h-3 w-3" />
                        {t('verifiedOwner')}
                      </span>
                    ) : null}
                    
                    <span className="inline-block px-3 py-1 bg-purple-100 text-purple-800 text-xs rounded-full mb-3">
                      {(() => {
                        const catMap = {
                          'Food & Dining': t('categoryFood', 'Food & Dining'),
                          'Music & Nightlife': t('categoryMusic', 'Music & Nightlife'),
                          'Family Activities': t('categories.family', 'Family Activities'),
                          'Special Offers': t('categories.offers', 'Special Offers'),
                          'Shopping/Retail': t('categoryShopping', 'Shopping/Retail'),
                          'Health & Wellness': t('categoryHealthWellness', 'Health & Wellness'),
                          'Events & Entertainment': t('categoryEvents', 'Events & Entertainment'),
                          'Education': t('categoryEducation', 'Education'),
                          'Home Services': t('categoryHomeServices', 'Home Services'),
                          'Travel & Tourism': t('categoryTravel', 'Travel & Tourism'),
                          'Beauty & Spa': t('categoryBeautySpa', 'Beauty & Spa'),
                          'Sports & Fitness': t('categorySports', 'Sports & Fitness'),
                          'Arts & Culture': t('categoryArts', 'Arts & Culture'),
                          'Professional Services': t('categoryProfessional', 'Professional Services'),
                          'Automotive': t('categoryAutomotive', 'Automotive'),
                          'Technology': t('categoryTechnology', 'Technology'),
                          'Vets': t('categoryVets', 'Vets'),
                          'Volunteer Work': t('categoryVolunteerWork', 'Volunteer Work'),
                          'Jobs': t('categoryJobs', 'Jobs'),
                          'Community Help': t('categoryCommunityHelp', 'Community Help'),
                          'Community': t('categoryCommunity', 'Community'),
                          'Other': t('categoryOther', 'Other'),
                        };
                        return catMap[business.category] || business.category;
                      })()}
                    </span>
                    
                    {(business.description_en || business.description_es) && (
                      <p className="text-sm text-gray-600 mb-3 line-clamp-2">
                        {i18n.language.startsWith('es') ? business.description_es : business.description_en}
                      </p>
                    )}
                    
                    <div className="flex items-center text-sm text-gray-500">
                      <MapPin className="h-4 w-4 mr-1" />
                      {business.city ? formatPlaceName(business.city) : business.address}
                      {business.distance !== undefined && business.distance !== null && (
                        <span className="ml-2 text-xs font-medium text-purple-600" data-testid={`distance-${business.id}`}>
                          {business.distance} km
                        </span>
                      )}
                    </div>

                    {/* Show feature tags */}
                    {(business.venueType?.length > 0 || business.additionalFeatures?.length > 0) && (
                      <div className="flex flex-wrap gap-1 mt-3">
                        {business.venueType?.slice(0, 2).map(v => (
                          <span key={v} className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded">
                            {tf(t, v)}
                          </span>
                        ))}
                        {business.additionalFeatures?.slice(0, 2).map(f => (
                          <span key={f} className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded">
                            {tf(t, f)}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {/* Filter Panel */}
      <FilterPanel 
        isOpen={showFilters} 
        onClose={() => setShowFilters(false)}
        onApplyFilters={handleApplyFilters}
        currentFilters={activeFilters}
      />

      {/* Bottom Navigation */}
      <BottomNavigation />

      {/* Favourite toast */}
      {favToast && (
        <div className="fixed bottom-24 left-1/2 -translate-x-1/2 z-50 px-4 py-2 bg-gray-900 text-white text-sm rounded-full shadow-lg animate-fade-in" data-testid="fav-toast">
          <Heart className="h-3.5 w-3.5 inline-block mr-1.5 fill-red-400 text-red-400" />{favToast}
        </div>
      )}
    </div>
  );
};

export default Discover;
